import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';
import useScrollAnimation from './hooks/useScrollAnimation';
import { ContentProvider } from './context/ContentContext';

const GA_MEASUREMENT_ID = 'G-ZQWZFCC9L4';
const HomePage = lazy(() => import('./pages/HomePage'));
const VenturesPage = lazy(() => import('./pages/VenturesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PressKitPage = lazy(() => import('./pages/PressKitPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BookLandingPage = lazy(() => import('./pages/BookLandingPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function GoogleAnalyticsPageTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    const pagePath = `${location.pathname}${location.search}${location.hash}`;

    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location]);

  return null;
}

function AppContent() {
  useScrollAnimation();
  const location = useLocation();
  const isBookLandingPage = location.pathname === '/project-2000' || location.pathname.startsWith('/landing/');
  const isFullBleedPage = isBookLandingPage;

  return (
    <div className="bg-primary min-h-screen">
      {!isFullBleedPage ? <Navigation /> : null}
      <ScrollToTop />
      <main className={isFullBleedPage ? 'pb-8' : 'max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-28 pb-8'}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/press-kit" element={<PressKitPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/ventures" element={<VenturesPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/project-2000" element={<BookLandingPage />} />
            <Route path="/landing/:landingSlug" element={<BookLandingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  return (
    <ContentProvider>
      <Router>
        <GoogleAnalyticsPageTracker />
        <AppContent />
      </Router>
    </ContentProvider>
  );
}

export default App;
